import api from './api';

export interface InvoiceLineItem {
  recno: number;
  invoiceRecno: number;
  pronumber: number;
  lineNumber: number;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
}

export interface InvoiceLineItemCreate {
  lineNumber: number;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
}

export type InvoiceLineItemUpdate = InvoiceLineItemCreate;

/** One row of the desired keep-set for the batch reconcile save. An existing
 *  row carries its `recno`; a new row omits it (undefined/null). */
export interface InvoiceLineItemBatchItem {
  recno?: number | null;
  lineNumber: number;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
}

/** `?accessorialnum=N` when targeting an accessorial (N>0); omitted for the main
 *  invoice (0) so the query string stays identical to the pre-accessorial calls. */
const accParam = (accessorialnum?: number): string =>
  accessorialnum && accessorialnum > 0 ? `?accessorialnum=${accessorialnum}` : '';

export const invoiceLineItemsService = {
  async list(pronumber: number | string, accessorialnum?: number): Promise<InvoiceLineItem[]> {
    const res = await api.get(
      `/orders/${pronumber}/invoice-line-items${accParam(accessorialnum)}`,
    );
    return res.data;
  },

  /** Batch-save the whole line-item set in one atomic request. The backend
   *  reconciles: inserts new rows, updates changed ones, and soft-removes any
   *  existing row absent from `lineItems`. Returns the fresh active rows.
   *  Pass `accessorialnum` (>0) to target an accessorial's line items instead of
   *  the main invoice. */
  async saveBatch(
    pronumber: number | string,
    lineItems: InvoiceLineItemBatchItem[],
    accessorialnum?: number,
    /** Admin Override (#33501): edit lines on an ALREADY-INVOICED invoice. Without it
     *  the backend 409s. Validated server-side against pro-admin-override, so passing
     *  true is a request rather than an authorization. */
    override?: boolean,
    /**
     * Pending Approval as the SAME user action is setting it, when the header and the
     * lines go as two requests (the accessorial modal; the Invoicing tab's cmsapi
     * route). Send it and the backend stands its re-flag down — resolving and
     * repricing in one act is one decision, and reverting it sent the invoice back to
     * Pending Approval on a user who had just resolved it.
     *
     * Not an instruction: nothing is written to the flag from this route. The server
     * re-validates it against the lines as saved before committing, so a RESOLVED
     * still has to earn it.
     */
    pendingApproval?: string,
  ): Promise<InvoiceLineItem[]> {
    const res = await api.put(
      `/orders/${pronumber}/invoice-line-items${accParam(accessorialnum)}`,
      {
        lineItems,
        ...(override ? { override: true } : {}),
        ...(pendingApproval !== undefined ? { pendingApproval } : {}),
      },
    );
    return res.data;
  },

  /** Change All: per-PRO eligibility + linumber/liitem-mismatch warnings for the flagged
   *  lines. Each line carries {isNew, linumber, item, ...editable fields}. */
  async changeAllPreview(
    pros: (number | string)[],
    lines: Array<Record<string, unknown>>,
    signal?: AbortSignal,
  ): Promise<{ rows: Array<{ pronumber: number | string; eligible: boolean; warnings?: string[]; errors: string[] }> }> {
    const res = await api.post(
      `/orders/change-all/invoice-li/preview`, { pros, lines }, { signal });
    return res.data;
  },

  /** Change All: add new lines / edit existing lines by position across every target PRO's
   *  main invoice ((nonce, pro)-idempotent). New lines append; edits match by line position
   *  with a liitem safety-check (mismatches are reported as warnings, not applied). */
  async changeAllApply(
    pros: (number | string)[],
    lines: Array<Record<string, unknown>>,
    submitNonce: string,
  ): Promise<{ results: Array<{ pronumber: number | string; applied: boolean; warnings?: string[]; errors: string[] }> }> {
    const res = await api.post(
      `/orders/change-all/invoice-li/apply`, { pros, lines, submitNonce });
    return res.data;
  },
};
